/**
 * Invoice Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  InvoiceEntity,
  InvoiceDataEntity,
  LineItemEntity,
  InvoicesResultEntity,
  InvoiceOperationResult,
  PaginationInfo,
} from "../../domain/entities/InvoiceEntity";
import {
  InvoiceDTO,
  InvoiceDataDTO,
  LineItemDTO,
  InvoicesResponseDTO,
  InvoiceOperationResponseDTO,
  PaginationDTO,
} from "../models/InvoiceDTOs";

/** Coerce API value (string or number) to number for tcs_tax_percentage */
function toTcsPercentNumber(raw: unknown): number | undefined {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return !Number.isNaN(n) && n >= 0 ? n : undefined;
}

/**
 * Helper function to parse invoice JSON if it's a string
 */
export const parseInvoiceJson = (
  invoiceJsonString: string | InvoiceDataDTO,
): InvoiceDataDTO | null => {
  if (!invoiceJsonString) {
    return null;
  }

  try {
    if (typeof invoiceJsonString === "string") {
      return JSON.parse(invoiceJsonString) as InvoiceDataDTO;
    }
    return invoiceJsonString;
  } catch (error) {
    console.error("Error parsing invoice_json:", error);
    return null;
  }
};

export class InvoiceMapper {
  /**
   * Map LineItemDTO to LineItemEntity
   */
  static toLineItemEntity(dto: LineItemDTO): LineItemEntity {
    return {
      name: dto.name,
      description: dto.description,
      sku: dto.sku,
      hsn_or_sac: dto.hsn_or_sac,
      hsn_description: dto.hsn_description,
      quantity: dto.quantity,
      rate: dto.rate,
      item_total: dto.item_total,
      item_order: dto.item_order,
      unit: dto.unit,
      account_id: dto.account_id,
      original_account_id: dto.original_account_id,
      account_name: dto.account_name,
      tax_id: dto.tax_id,
      tax_name: dto.tax_name,
      tax_percentage: dto.tax_percentage,
      tax_amount: dto.tax_amount,
      item_total_inclusive_of_tax: dto.item_total_inclusive_of_tax,
      is_billable: dto.is_billable,
      gl_recommendation: dto.gl_recommendation,
      gl_confidence: dto.gl_confidence,
      gl_source_layer: dto.gl_source_layer,
      tds_tax_id: dto.tds_tax_id,
      tds_tax_percentage: dto.tds_tax_percentage,
      tds_tax_name: dto.tds_tax_name,
    };
  }

  /**
   * Map LineItemEntity to LineItemDTO
   */
  static toLineItemDTO(entity: LineItemEntity): LineItemDTO {
    return {
      name: entity.name,
      description: entity.description,
      sku: entity.sku,
      hsn_or_sac: entity.hsn_or_sac,
      hsn_description: entity.hsn_description,
      quantity: entity.quantity,
      rate: entity.rate,
      item_total: entity.item_total,
      item_order: entity.item_order,
      unit: entity.unit,
      account_id: entity.account_id,
      original_account_id: entity.original_account_id,
      account_name: entity.account_name,
      tax_id: entity.tax_id,
      tax_name: entity.tax_name,
      tax_percentage: entity.tax_percentage,
      tax_amount: entity.tax_amount,
      item_total_inclusive_of_tax: entity.item_total_inclusive_of_tax,
      is_billable: entity.is_billable,
      gl_recommendation: entity.gl_recommendation,
      gl_confidence: entity.gl_confidence,
      gl_source_layer: entity.gl_source_layer,
      tds_tax_id: entity.tds_tax_id,
      tds_tax_percentage: entity.tds_tax_percentage,
      tds_tax_name: entity.tds_tax_name,
    };
  }

  /**
   * Map InvoiceDataDTO to InvoiceDataEntity
   */
  static toInvoiceDataEntity(dto: InvoiceDataDTO): InvoiceDataEntity {
    return {
      customer_id: dto.customer_id,
      // ZOUR-65: which GST registration issued this invoice. These mappers are
      // explicit field lists — omit it and every UI save strips it while every
      // load drops it, so the picker's choice never survives a reload.
      location_id: dto.location_id ?? null,
      // ZOUR-65 R11: the pipeline's location match. Same trap as location_id
      // (F9) — these mappers are explicit field lists, so leaving it out meant
      // the page never sent the stored match and the flags recomputed from
      // nothing every time.
      location_match: dto.location_match ?? null,
      invoice_number: dto.invoice_number,
      discount: dto.discount,
      date: dto.date,
      due_date: dto.due_date,
      reference_number: dto.reference_number,
      currency_id: dto.currency_id,
      currency_code: dto.currency_code as any, // Accept any currency code
      currency_symbol: dto.currency_symbol,
      exchange_rate: dto.exchange_rate,
      vendor_name: dto.vendor_name,
      vendor_gstin: dto.vendor_gstin,
      gst_no: dto.gst_no,
      gst_treatment: dto.gst_treatment as any, // Accept any GST treatment
      gst_type: dto.gst_type as InvoiceDataEntity["gst_type"],
      is_igst: dto.is_igst,

      customer_name: dto.customer_name,
      customer_gstin: dto.customer_gstin,
      source_of_supply: dto.source_of_supply,
      destination_of_supply: dto.destination_of_supply,
      place_of_supply: dto.place_of_supply,
      invoice_date: dto.invoice_date,
      eway_bill_number: dto.eway_bill_number,
      irn_number: dto.irn_number,
      line_items: (dto.line_items ?? []).map((item) =>
        this.toLineItemEntity(item),
      ),
      sub_total: dto.sub_total,
      tax_total: dto.tax_total,
      total: dto.total,
      payment_terms: dto.payment_terms,
      payment_terms_label: dto.payment_terms_label,
      is_item_level_tax_calc: dto.is_item_level_tax_calc,
      is_inclusive_tax: dto.is_inclusive_tax,
      notes: dto.notes,
      terms: dto.terms,
      is_discount_before_tax: dto.is_discount_before_tax,
      discount_type: dto.discount_type,
      tds_amount: dto.tds_amount,
      tds_percent: dto.tds_percent,
      processing_metadata: dto.processing_metadata,
      validation_results: dto.validation_results,
      customer_address: dto.customer_address,
      tcs_tax_id: dto.tcs_tax_id,
      tcs_tax_name: dto.tcs_tax_name,
      tcs_tax_percentage: toTcsPercentNumber(
        (dto as { tcs_tax_percentage?: unknown }).tcs_tax_percentage,
      ),
      tcs_tax_amount: dto.tcs_tax_amount,
      tcs_nature_of_collection: dto.tcs_nature_of_collection,
      tds_tax_amount: dto.tds_tax_amount,
    };
  }

  /**
   * Map InvoiceDataEntity to InvoiceDataDTO
   */
  static toInvoiceDataDTO(entity: InvoiceDataEntity): InvoiceDataDTO {
    return {
      customer_id: entity.customer_id,
      // ZOUR-65: see the note on the entity mapper above.
      location_id: entity.location_id ?? null,
      // ZOUR-65 R11: see the note on the entity mapper.
      location_match: entity.location_match ?? null,
      invoice_number: entity.invoice_number,
      discount: entity.discount,
      date: entity.date,
      due_date: entity.due_date,
      reference_number: entity.reference_number,
      currency_id: entity.currency_id,
      currency_code: entity.currency_code as any, // Accept any currency code
      currency_symbol: entity.currency_symbol,
      exchange_rate: entity.exchange_rate,
      vendor_name: entity.vendor_name,
      vendor_gstin: entity.vendor_gstin,
      gst_no: entity.gst_no,
      gst_treatment: entity.gst_treatment,
      gst_type: entity.gst_type,
      is_igst: entity.is_igst,
      customer_name: entity.customer_name,
      customer_gstin: entity.customer_gstin,
      source_of_supply: entity.source_of_supply,
      destination_of_supply: entity.destination_of_supply,
      place_of_supply: entity.place_of_supply,
      invoice_date: entity.invoice_date,
      eway_bill_number: entity.eway_bill_number,
      irn_number: entity.irn_number,
      line_items: (entity.line_items ?? []).map((item) =>
        this.toLineItemDTO(item),
      ),
      sub_total: entity.sub_total,
      tax_total: entity.tax_total,
      total: entity.total,
      payment_terms: entity.payment_terms,
      payment_terms_label: entity.payment_terms_label,
      is_item_level_tax_calc: entity.is_item_level_tax_calc,
      is_inclusive_tax: entity.is_inclusive_tax,
      notes: entity.notes,
      terms: entity.terms,
      is_discount_before_tax: entity.is_discount_before_tax,
      discount_type: entity.discount_type,
      tds_amount: entity.tds_amount,
      tds_percent: entity.tds_percent,
      processing_metadata: entity.processing_metadata,
      validation_results: entity.validation_results,
      customer_address: entity.customer_address,
      tcs_tax_id: entity.tcs_tax_id,
      tcs_tax_name: entity.tcs_tax_name,
      tcs_tax_percentage: entity.tcs_tax_percentage,
      tcs_tax_amount: entity.tcs_tax_amount,
      tcs_nature_of_collection: entity.tcs_nature_of_collection,
      tds_tax_amount: entity.tds_tax_amount,
    };
  }

  /**
   * Map InvoiceDTO to InvoiceEntity
   * Handles string or object invoice_json from API
   */
  static toInvoiceEntity(dto: InvoiceDTO): InvoiceEntity {
    // Parse invoice_json if it's a string
    const invoiceJsonParsed =
      typeof dto.invoice_json === "string"
        ? parseInvoiceJson(dto.invoice_json)
        : dto.invoice_json;

    return {
      id: dto.id,
      jobid: dto.jobid,
      companyid: dto.companyid,
      filename: dto.filename,
      original_filename: dto.original_filename,
      file_size: dto.file_size,
      folder_id: dto.folder_id,
      invoice_json: invoiceJsonParsed
        ? this.toInvoiceDataEntity(invoiceJsonParsed)
        : ({} as InvoiceDataEntity),
      invoice_candidates: dto.invoice_candidates,
      customer_vector: dto.customer_vector,
      posted_to_zoho_date: dto.posted_to_zoho_date,
      gst_rates: dto.gst_rates,
      gst_type: dto.gst_type,
      reviewed: dto.reviewed,
      status: dto.status,
      on_hold: dto.on_hold ?? 0, // Default to 0 (not on hold) if undefined
      is_under_review:
        dto.is_under_review !== undefined ? Number(dto.is_under_review) : undefined,
      open_queries_count: dto.open_queries_count,
      source: dto.source,
      write_off_status: dto.write_off_status,
      zoho_invoiceid: dto.zoho_invoiceid,
      deleted: dto.deleted,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      signed_url: dto.signed_url,
      signed_url_expires_at: dto.signed_url_expires_at,
      attachment_urls: dto.attachment_urls,
      customer_match_method: dto.customer_match_method,
      customer_match_details: dto.customer_match_details,
      gl_line_suggestions: dto.gl_line_suggestions,
    };
  }

  /**
   * Map InvoiceEntity to InvoiceDTO
   */
  static toInvoiceDTO(entity: InvoiceEntity): InvoiceDTO {
    return {
      id: entity.id,
      jobid: entity.jobid,
      companyid: entity.companyid,
      filename: entity.filename,
      original_filename: entity.original_filename,
      file_size: entity.file_size,
      folder_id: entity.folder_id,
      invoice_json: this.toInvoiceDataDTO(entity.invoice_json),
      invoice_candidates: entity.invoice_candidates,
      customer_vector: entity.customer_vector,
      posted_to_zoho_date: entity.posted_to_zoho_date,
      gst_rates: entity.gst_rates,
      gst_type: entity.gst_type,
      reviewed: entity.reviewed,
      status: entity.status,
      on_hold: entity.on_hold,
      is_under_review: entity.is_under_review,
      open_queries_count: entity.open_queries_count,
      source: entity.source,
      write_off_status: entity.write_off_status,
      zoho_invoiceid: entity.zoho_invoiceid,
      deleted: entity.deleted,
      created_at: entity.created_at,
      updated_at: entity.updated_at,
      signed_url: entity.signed_url,
      signed_url_expires_at: entity.signed_url_expires_at,
      attachment_urls: entity.attachment_urls,
    };
  }

  /**
   * Map PaginationDTO to PaginationInfo
   */
  static toPaginationInfo(dto: PaginationDTO): PaginationInfo {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      has_more: dto.has_more,
      current_page: dto.current_page,
      total_pages: dto.total_pages,
    };
  }

  /**
   * Map InvoicesResponseDTO to InvoicesResultEntity
   */
  static toInvoicesResultEntity(
    dto: InvoicesResponseDTO,
  ): InvoicesResultEntity {
    return {
      invoices: dto.data.invoices.map((invoice) =>
        this.toInvoiceEntity(invoice),
      ),
      pagination: this.toPaginationInfo(dto.data.pagination),
    };
  }

  /**
   * Map InvoiceOperationResponseDTO to InvoiceOperationResult
   */
  static toInvoiceOperationResult(
    dto: InvoiceOperationResponseDTO,
  ): InvoiceOperationResult {
    return {
      success: dto.success,
      message: dto.message,
      invoice: dto.data ? this.toInvoiceEntity(dto.data) : undefined,
      error: dto.error,
    };
  }
}
